import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import * as turf from '@turf/turf';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MapPin, CheckCircle2, AlertCircle, Navigation } from 'lucide-react';
import api from './api';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import toast, { Toaster } from 'react-hot-toast';

mapboxgl.accessToken = 'pk.eyJ1IjoiYm91YnNvbiIsImEiOiJjbTY5Y3Zkc2QwN2lrMmpzaGp5c2RlcngyIn0.gPzN_xXjA0fHABHXeOXHVQ';

const styles = [
  {
    title: "Light",
    uri: "mapbox://styles/mapbox/light-v11"
  },
  {
    title: "Dark",
    uri: "mapbox://styles/mapbox/dark-v11"
  },
  {
    title: "Streets",
    uri: "mapbox://styles/mapbox/streets-v12"
  },
  {
    title: "Outdoors",
    uri: "mapbox://styles/mapbox/outdoors-v12"
  },
  {
    title: "Satellite",
    uri: "mapbox://styles/mapbox/satellite-streets-v12"
  }
];

const MapBoxComponent = () => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const draw = useRef(null);
  const [activeStyle, setActiveStyle] = useState('satellite-streets');
  const [is3DMode, setIs3DMode] = useState(false);
  const [depotsData, setDepotsData] = useState(null);
  const [communesData, setCommunesData] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [route, setRoute] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentStyle, setCurrentStyle] = useState('satellite-streets-v12');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [filteredCommunes, setFilteredCommunes] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Récupérer les données des dépôts
        const depotsResponse = await api.get('/collect/data-map/');
        const geoJsonData = {
          type: 'FeatureCollection',
          features: depotsResponse.data.features
        };
        setDepotsData(geoJsonData);

        // Récupérer les données des communes
        const communesResponse = await api.get('/collect/commune-status/');
        console.log('Données des communes:', communesResponse.data);
        setCommunesData(communesResponse.data);

        setIsLoading(false);
      } catch (error) {
        console.error('Erreur lors de la récupération des données:', error);
        setError('Erreur lors du chargement des données');
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    // Demander la géolocalisation au chargement
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.error('Erreur de géolocalisation:', error);
        }
      );
    }
  }, []);

  // Fonction pour basculer le mode 3D
  const toggle3DMode = () => {
    setIs3DMode(!is3DMode);
    if (map.current) {
      map.current.setPitch(is3DMode ? 0 : 60);
      map.current.setBearing(is3DMode ? 0 : 30);
    }
  };

  // Fonction modifiée pour le popup
  const createPopupContent = (feature) => {
    const defaultImage = '/depot.jpg';
    const properties = feature.properties || {};
    const imageUrl = properties.devanture_url || defaultImage;

    // Extraire les objets imbriqués
    const depot_info = properties.depot_info || {};
    const proprietaire = properties.proprietaire || {};
    const gerant = properties.gerant || {};
    const infrastructure = properties.infrastructure || {};

    return `
      <div class="w-[400px] bg-white rounded-xl overflow-hidden">
        <div class="p-4">
          <div class="flex justify-between items-start mb-4">
            <div class="flex space-x-4">
              <div class="w-2">
                <img 
                  src="${imageUrl}" 
                  alt="Devanture du dépôt" 
                  class="w-full h-24 object-cover rounded-lg shadow-sm"
                  onerror="this.src='${defaultImage}'"
                />
              </div>
              <div class="w-2/3">
                <h3 class="text-base font-semibold text-gray-900 mb-1">${properties.title}</h3>
                <div class="flex items-center mb-2">
                  <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${properties.status === 'Avec numéro d\'autorisation'
        ? 'bg-green-100 text-green-800'
        : 'bg-orange-100 text-orange-800'
      }">
                    ${properties.status}
                  </span>
                </div>
                <p class="text-xs text-gray-500 mb-1">${properties.adresse}</p>
                <p class="text-xs text-gray-500">${properties.commune}, ${properties.region}</p>
              </div>
            </div>
            <button 
              onclick="this.closest('.mapboxgl-popup').remove()"
              class="text-gray-400 hover:text-gray-600"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div class="mt-3 pt-3 border-t border-gray-100">
            <div class="grid grid-cols-2 gap-2 text-xs text-gray-600">
              <div>
                <p><span class="font-medium">N° Autorisation:</span> ${depot_info.numero_autorisation}</p>
                <p><span class="font-medium">Date création:</span> ${depot_info.date_creation}</p>
                <p><span class="font-medium">Surface:</span> ${infrastructure.surface}</p>
                <p><span class="font-medium">Climatisation:</span> ${infrastructure.climatisation}</p>
                <p><span class="font-medium">Réfrigérateur:</span> ${infrastructure.refrigerateur}</p>
              </div>
              <div>
                <p><span class="font-medium">Propriétaire:</span> ${proprietaire.nom}</p>
                <p><span class="font-medium">Tél. Propriétaire:</span> ${proprietaire.telephone}</p>
                <p><span class="font-medium">Gérant:</span> ${gerant.nom}</p>
                <p><span class="font-medium">Tél. Gérant:</span> ${gerant.telephone}</p>
                <p><span class="font-medium">Département:</span> ${properties.departement}</p>
              </div>
            </div>
          </div>

          <div class="mt-3 pt-3 border-t border-gray-100">
            <div class="text-xs text-gray-600">
              <p><span class="font-medium">Localité:</span> ${properties.localite}</p>
              <p><span class="font-medium">Quartier:</span> ${properties.quartier}</p>
              <p><span class="font-medium">Source d'énergie:</span> ${infrastructure.source_energie}</p>
            </div>
          </div>

          <div class="flex space-x-2 mt-3">
            
            <button 
              onclick="window.calculateRoute(${JSON.stringify(feature.geometry.coordinates)})"
              class="flex-1 inline-flex items-center justify-center px-2 py-1.5 text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
            >
              <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
              Itinéraire
            </button>
          </div>
        </div>
      </div>
    `;
  };

  // Fonction pour calculer et afficher l'itinéraire
  const calculateRoute = async (destination) => {
    if (!userLocation) {
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject);
        });

        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
      } catch (error) {
        alert("Veuillez activer la géolocalisation pour voir l'itinéraire.");
        return;
      }
    }

    try {
      // Calcul de l'itinéraire
      const response = await fetch(
        `https://api.mapbox.com/directions/v5/mapbox/driving/${userLocation.lng},${userLocation.lat};${destination[0]},${destination[1]}?geometries=geojson&access_token=${mapboxgl.accessToken}`
      );
      const data = await response.json();
      const route = data.routes[0];
      const distance = (route.distance / 1000).toFixed(2); // Distance en km

      // Mettre à jour la ligne de l'itinéraire sur la carte
      if (map.current.getSource('route')) {
        map.current.getSource('route').setData({
          'type': 'Feature',
          'properties': {},
          'geometry': route.geometry
        });
      }

      // Ajuster la vue pour voir tout l'itinéraire
      const coordinates = route.geometry.coordinates;
      const bounds = coordinates.reduce((bounds, coord) => {
        return bounds.extend(coord);
      }, new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]));

      map.current.fitBounds(bounds, {
        padding: 50,
        duration: 1000
      });

      // Ajouter les marqueurs de départ et d'arrivée
      // Supprimer les anciens marqueurs s'ils existent
      if (window.startMarker) window.startMarker.remove();
      if (window.endMarker) window.endMarker.remove();

      // Marqueur de départ
      window.startMarker = new mapboxgl.Marker({ color: '#3b82f6' })
        .setLngLat([userLocation.lng, userLocation.lat])
        .addTo(map.current);

      // Marqueur d'arrivée
      window.endMarker = new mapboxgl.Marker({ color: '#ef4444' })
        .setLngLat(destination)
        .addTo(map.current);

      // Calcul des temps pour différents modes de transport
      const speedModes = {
        driving: 50,
        cycling: 15,
        walking: 5
      };

      const times = {};
      for (const [mode, speed] of Object.entries(speedModes)) {
        const timeInHours = distance / speed;
        const hours = Math.floor(timeInHours);
        const minutes = Math.round((timeInHours - hours) * 60);
        times[mode] = hours > 0 ? `${hours}h ${minutes}min` : `${minutes}min`;
      }

      // Afficher le popup avec les informations
      const popupContent = `
        <div class="p-4 bg-white rounded-lg shadow-lg">
          <h3 class="text-lg font-semibold mb-3">Estimation du trajet</h3>
          <p class="text-sm mb-2"><strong>Distance:</strong> ${distance} km</p>
          <div class="space-y-2">
            <p class="text-sm">
              <span class="inline-block w-6">🚗</span> 
              <strong>En voiture:</strong> ${times.driving}
            </p>
            <p class="text-sm">
              <span class="inline-block w-6">🚲</span> 
              <strong>À vélo:</strong> ${times.cycling}
            </p>
            <p class="text-sm">
              <span class="inline-block w-6">🚶</span> 
              <strong>À pied:</strong> ${times.walking}
            </p>
          </div>
        </div>
      `;

      new mapboxgl.Popup()
        .setLngLat(destination)
        .setHTML(popupContent)
        .addTo(map.current);

    } catch (error) {
      console.error('Erreur lors du calcul de l\'itinéraire:', error);
      alert('Erreur lors du calcul de l\'itinéraire');
    }
  };

  // Fonction pour afficher plus de détails
  const showMoreDetails = (properties) => {
    // Créer une modal plus détaillée
    const modal = document.createElement('div');
    modal.className = 'fixed inset-0 z-50 overflow-y-auto';
    modal.innerHTML = `
      <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
        <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <!-- Contenu détaillé -->
            <div class="space-y-4">
              <h3 class="text-lg font-medium text-gray-900">${properties.title}</h3>
              
              <div class="mt-4 space-y-2">
                <h4 class="font-medium">Infrastructure</h4>
                <p>Surface: ${properties.infrastructure.surface}</p>
                <p>Climatisation: ${properties.infrastructure.climatisation}</p>
                <p>Réfrigérateur: ${properties.infrastructure.refrigerateur}</p>
                <p>Source d'énergie: ${properties.infrastructure.source_energie}</p>
              </div>

              <div class="mt-4 space-y-2">
                <h4 class="font-medium">Gérant</h4>
                <p>Nom: ${properties.gerant.nom}</p>
                <p>Téléphone: ${properties.gerant.telephone}</p>
                <p>Email: ${properties.gerant.email}</p>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button type="button" class="close-modal mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm">
              Fermer
            </button>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(modal);
    modal.querySelector('.close-modal').onclick = () => modal.remove();
  };

  // Rendre la fonction calculateRoute disponible globalement
  window.calculateRoute = calculateRoute;
  window.showMoreDetails = showMoreDetails;

  useEffect(() => {
    if (!mapboxgl.accessToken || !mapContainer.current || map.current || !depotsData || !communesData) return;

    const initializeMap = () => {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/satellite-streets-v12',
        center: [8, 17],
        zoom: 5,
        maxBounds: [[-2, 8], [20, 28]],
        pitch: is3DMode ? 45 : 0,
        minZoom: 5,
        maxZoom: 20
      });

      map.current.on('load', () => {
        // Initialisation de la source et de la couche de l'itinéraire
        if (!map.current.getSource('route')) {
          map.current.addSource('route', {
            'type': 'geojson',
            'data': {
              'type': 'Feature',
              'properties': {},
              'geometry': {
                'type': 'LineString',
                'coordinates': []
              }
            }
          });

          map.current.addLayer({
            'id': 'route',
            'type': 'line',
            'source': 'route',
            'layout': {
              'line-join': 'round',
              'line-cap': 'round'
            },
            'paint': {
              'line-color': '#3b82f6',
              'line-width': 4,
              'line-opacity': 0.75
            }
          });
        }

        console.log('Données des communes à ajouter à la carte:', communesData);

        // Vérifier si les données sont au bon format
        if (!communesData || !Array.isArray(communesData)) {
          console.error('Format de données des communes invalide');
          return;
        }

        // Ajouter les sources et couches pour les dépôts et communes
        if (communesData && Array.isArray(communesData)) {
          map.current.addSource('communes', {
            type: 'geojson',
            data: {
              type: 'FeatureCollection',
              features: communesData.map(commune => ({
                type: 'Feature',
                properties: {
                  commune: commune.commune,
                  population: commune.population,
                  depot_count: commune.depot_count,
                  has_officine: commune.has_officine,
                  status: commune.status
                },
                geometry: commune.geometry
              }))
            }
          });

          map.current.addLayer({
            id: 'communes-fill',
            type: 'fill',
            source: 'communes',
            paint: {
              'fill-color': [
                'match',
                ['get', 'status'],
                'eligible', '#4CAF50',
                'ineligible_population', '#FF9800',
                'ineligible_depots', '#F44336',
                'ineligible_officine', '#9C27B0',
                'ineligible_urbain', '#795548',
                '#9E9E9E'
              ],
              'fill-opacity': 0.7
            }
          });

          map.current.addLayer({
            id: 'communes-outline',
            type: 'line',
            source: 'communes',
            paint: {
              'line-color': '#627BC1',
              'line-width': 1
            }
          });
        }

        // Légende des communes
        const legendContainer = document.createElement('div');
        legendContainer.className = 'absolute bottom-8 left-4 z-10 bg-white/90 backdrop-blur-sm rounded-lg shadow-sm border border-gray-100 p-4';
       

        // Ajouter les événements pour les interactions avec les communes
        map.current.on('mouseenter', 'communes-fill', () => {
          map.current.getCanvas().style.cursor = 'pointer';
        });

        map.current.on('mouseleave', 'communes-fill', () => {
          map.current.getCanvas().style.cursor = '';
        });

        map.current.on('click', 'communes-fill', (e) => {
          if (!e.features || !e.features.length) return;

          const feature = e.features[0];
          const properties = feature.properties;
          const coordinates = e.lngLat;

          // Créer le contenu du popup
          const popupContent = `
            <div class="p-4 bg-white rounded-lg shadow-lg">
              <h3 class="text-lg font-semibold mb-2">${properties.commune}</h3>
              <div class="space-y-2">
                <p class="text-sm">
                  <span class="font-medium">Population:</span> ${properties.population.toLocaleString()} habitants
                </p>
                <p class="text-sm">
                  <span class="font-medium">Nombre de dépôts:</span> ${properties.depot_count}
                </p>
                <p class="text-sm">
                  <span class="font-medium">Officine:</span> ${properties.has_officine ? 'Présente' : 'Absente'}
                </p>
                <p class="text-sm">
                  <span class="font-medium">Statut:</span> 
                  <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    properties.status === 'eligible' ? 'bg-green-100 text-green-800' :
                    properties.status === 'ineligible_population' ? 'bg-orange-100 text-orange-800' :
                    properties.status === 'ineligible_depots' ? 'bg-red-100 text-red-800' :
                    properties.status === 'ineligible_officine' ? 'bg-purple-100 text-purple-800' :
                    'bg-brown-100 text-brown-800'
                  }">
                    ${properties.status === 'eligible' ? 'Éligible' :
                      properties.status === 'ineligible_population' ? 'Population insuffisante' :
                      properties.status === 'ineligible_depots' ? 'Trop de dépôts' :
                      properties.status === 'ineligible_officine' ? 'Officine présente' :
                      'Zone urbaine'}
                  </span>
                </p>
              </div>
            </div>
          `;

          // Supprimer les popups existants
          const popups = document.getElementsByClassName('mapboxgl-popup');
          if (popups.length) {
            popups[0].remove();
          }

          // Créer et afficher le nouveau popup
          new mapboxgl.Popup()
            .setLngLat(coordinates)
            .setHTML(popupContent)
            .addTo(map.current);
        });

        // Mettre à jour le style au survol
        map.current.on('mousemove', 'communes-fill', (e) => {
          if (!e.features || !e.features.length) return;

          // Mettre à jour le style de la commune survolée
          map.current.setFilter('communes-fill-hover', ['==', ['get', 'commune'], e.features[0].properties.commune]);
        });

        map.current.on('mouseleave', 'communes-fill', () => {
          // Réinitialiser le style au survol
          map.current.setFilter('communes-fill-hover', ['==', ['get', 'commune'], '']);
        });

        // Source des dépôts
        map.current.addSource('depots', {
          type: 'geojson',
          data: depotsData,
          cluster: true,
          clusterMaxZoom: 14,
          clusterRadius: 50
        });

        // Couche des clusters
        map.current.addLayer({
          id: 'clusters',
          type: 'circle',
          source: 'depots',
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': [
              'step',
              ['get', 'point_count'],
              '#10B981',
              4,
              '#3B82F6',
              8,
              '#8B5CF6'
            ],
            'circle-radius': [
              'step',
              ['get', 'point_count'],
              25,
              4,
              35,
              8,
              45
            ],
            'circle-opacity': 0.7,
            'circle-stroke-width': 2,
            'circle-stroke-color': [
              'step',
              ['get', 'point_count'],
              '#059669',
              4,
              '#2563EB',
              8,
              '#6D28D9'
            ]
          }
        });

        // Couche du nombre dans les clusters
        map.current.addLayer({
          id: 'cluster-count',
          type: 'symbol',
          source: 'depots',
          filter: ['has', 'point_count'],
          layout: {
            'text-field': '{point_count_abbreviated}',
            'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
            'text-size': 14
          },
          paint: {
            'text-color': '#FFFFFF'
          }
        });

        // Couche des points individuels
        map.current.addLayer({
          id: 'unclustered-point',
          type: 'circle',
          source: 'depots',
          filter: ['!', ['has', 'point_count']],
          paint: {
            'circle-color': [
              'match',
              ['get', 'status'],
              'Reconnu', '#10B981',
              'Non Reconnu', '#F97316',
              '#059669'
            ],
            'circle-radius': 8,
            'circle-stroke-width': 2,
            'circle-stroke-color': '#fff'
          }
        });

        // Ajouter les événements pour les interactions avec les dépôts
        map.current.on('click', 'unclustered-point', (e) => {
          if (!e.features || !e.features.length) return;

          const feature = e.features[0];
          const coordinates = e.lngLat;

          
        });

        // Changer le curseur au survol
        map.current.on('mouseenter', 'unclustered-point', () => {
          map.current.getCanvas().style.cursor = 'pointer';
        });

        map.current.on('mouseleave', 'unclustered-point', () => {
          map.current.getCanvas().style.cursor = '';
        });

        // Créer un contrôle 3D personnalisé
        class ThreeDModeControl {
          onAdd(map) {
            this._map = map;
            this._container = document.createElement('div');
            this._container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';
            this._button = document.createElement('button');
            this._button.className = 'mapboxgl-ctrl-icon';
            this._button.type = 'button';
            this._button.style.padding = '6px';
            this._button.style.width = '30px';
            this._button.style.height = '30px';
            this._button.innerHTML = `<span style="font-weight: bold;">${is3DMode ? '2D' : '3D'}</span>`;
            this._button.onclick = () => {
              const newMode = !is3DMode;

              // Transition douce pour le pitch et le bearing
              map.easeTo({
                pitch: newMode ? 60 : 0,
                bearing: newMode ? 30 : 0,
                duration: 1000,
                essential: true
              });

              // Mettre à jour le texte du bouton
              this._button.innerHTML = `<span style="font-weight: bold;">${newMode ? '2D' : '3D'}</span>`;

              // Mettre à jour l'état après l'animation
              setIs3DMode(newMode);
            };
            this._container.appendChild(this._button);
            return this._container;
          }

          onRemove() {
            this._container.parentNode.removeChild(this._container);
            this._map = undefined;
          }
        }

        // Ajouter les contrôles dans l'ordre souhaité
        map.current.addControl(new mapboxgl.NavigationControl({ visualizePitch: true }), 'top-right');
        map.current.addControl(new ThreeDModeControl(), 'top-right');
        map.current.addControl(new mapboxgl.FullscreenControl(), 'top-right');
        map.current.addControl(new mapboxgl.ScaleControl(), 'bottom-right');

        // Modification du contrôle de recherche
        const searchControl = new MapboxGeocoder({
          accessToken: mapboxgl.accessToken,
          mapboxgl: mapboxgl,
          marker: false,
          localGeocoder: (query) => {
            const matches = depotsData.features.filter(feature =>
              feature.properties.title.toLowerCase().includes(query.toLowerCase()) ||
              feature.properties.region.toLowerCase().includes(query.toLowerCase()) ||
              feature.properties.commune.toLowerCase().includes(query.toLowerCase())
            );
            return matches.map(match => ({
              center: match.geometry.coordinates,
              geometry: match.geometry,
              place_name: `${match.properties.title} - ${match.properties.commune}, ${match.properties.region}`,
              properties: match.properties,
              type: 'Feature'
            }));
          },
          placeholder: 'Rechercher un dépôt...',
          externalGeocoder: null,
          localGeocoderOnly: true,
          render: function (item) {
            return `
            <div class="p-2">
              <div class="font-medium">${item.properties.title}</div>
              <div class="text-sm text-gray-500">
                ${item.properties.commune}, ${item.properties.region}
              </div>
            </div>
          `;
          }
        });
        map.current.addControl(searchControl, 'top-left');

        // Ajouter une source pour l'effet de surbrillance de recherche
        map.current.on('load', () => {
          map.current.addSource('search-result', {
            type: 'geojson',
            data: {
              type: 'FeatureCollection',
              features: []
            }
          });

          // Ajouter une couche pour l'effet de pulse
          map.current.addLayer({
            id: 'search-result-pulse',
            type: 'circle',
            source: 'search-result',
            paint: {
              'circle-radius': [
                'interpolate',
                ['linear'],
                ['get', 'pulse'],
                0, 15,
                1, 25
              ],
              'circle-color': '#10B981',
              'circle-opacity': [
                'interpolate',
                ['linear'],
                ['get', 'pulse'],
                0, 0.5,
                1, 0
              ],
              'circle-stroke-width': 2,
              'circle-stroke-color': '#10B981'
            }
          });

          // Gérer l'événement de résultat
          searchControl.on('result', (event) => {
            const feature = event.result;

            // Animer la carte vers le résultat
            map.current.flyTo({
              center: feature.geometry.coordinates,
              zoom: 15,
              duration: 2000
            });

            // Mettre à jour la source avec le point trouvé
            map.current.getSource('search-result').setData({
              type: 'FeatureCollection',
              features: [{
                type: 'Feature',
                geometry: feature.geometry,
                properties: { pulse: 0 }
              }]
            });

            // Animation de pulse
            let start;
            function animate(timestamp) {
              if (!start) start = timestamp;
              const progress = (timestamp - start) / 1500; // 1.5 secondes d'animation

              map.current.getSource('search-result').setData({
                type: 'FeatureCollection',
                features: [{
                  type: 'Feature',
                  geometry: feature.geometry,
                  properties: { pulse: progress }
                }]
              });

              if (progress < 1) {
                requestAnimationFrame(animate);
              }
            }

            requestAnimationFrame(animate);
          });

          // Nettoyer l'effet quand une nouvelle recherche commence
          searchControl.on('clear', () => {
            map.current.getSource('search-result').setData({
              type: 'FeatureCollection',
              features: []
            });
          });
        });

        // Ajout du contrôle de mesure de distance
        const distanceControl = new mapboxgl.ScaleControl({
          maxWidth: 100,
          unit: 'metric'
        });
        map.current.addControl(distanceControl, 'bottom-right');

        // Ajout du contrôle de rotation
        const navControl = new mapboxgl.NavigationControl({
          visualizePitch: true
        });
        map.current.addControl(navControl, 'top-right');

        // Amélioration du clustering
        map.current.on('load', () => {
          // ... code existant du clustering ...

          // Amélioration de l'animation de zoom sur les clusters
          map.current.on('click', 'clusters', (e) => {
            const features = map.current.queryRenderedFeatures(e.point, { layers: ['clusters'] });
            const clusterId = features[0].properties.cluster_id;

            map.current.getSource('depots').getClusterExpansionZoom(clusterId, (err, zoom) => {
              if (err) return;

              map.current.easeTo({
                center: features[0].geometry.coordinates,
                zoom: zoom,
                duration: 500,  // Animation plus fluide
                easing: (t) => t * (2 - t) // Animation plus naturelle
              });
            });
          });
        });

        // Amélioration de l'interaction avec les points
        map.current.on('mouseenter', 'unclustered-point', (e) => {
          map.current.getCanvas().style.cursor = 'pointer';
          const coordinates = e.features[0].geometry.coordinates.slice();
          const properties = e.features[0].properties;

          // Petit effet de pulse sur le point survolé
          map.current.setPaintProperty('unclustered-point', 'circle-radius', [
            'case',
            ['==', ['id'], e.features[0].id],
            12,  // taille augmentée pour le point survolé
            8    // taille normale pour les autres points
          ]);
        });

        map.current.on('mouseleave', 'unclustered-point', () => {
          map.current.getCanvas().style.cursor = '';
          // Retour à la taille normale
          map.current.setPaintProperty('unclustered-point', 'circle-radius', 8);
        });

        setupEventListeners();
      });
    };

    const setupEventListeners = () => {
      // Clic sur les clusters
      map.current.on('click', 'clusters', (e) => {
        const features = map.current.queryRenderedFeatures(e.point, { layers: ['clusters'] });
        const clusterId = features[0].properties.cluster_id;

        map.current.getSource('depots').getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return;
          map.current.easeTo({
            center: features[0].geometry.coordinates,
            zoom: zoom
          });
        });
      });

      // Clic sur les points
      map.current.on('click', 'unclustered-point', (e) => {
        if (!e.features || !e.features.length) return;

        const feature = e.features[0];
        const coordinates = feature.geometry.coordinates.slice();
        const properties = feature.properties;

        // Convertir les objets string en objets JS
        const depot_info = typeof properties.depot_info === 'string'
          ? JSON.parse(properties.depot_info)
          : properties.depot_info;

        const infrastructure = typeof properties.infrastructure === 'string'
          ? JSON.parse(properties.infrastructure)
          : properties.infrastructure;

        const proprietaire = typeof properties.proprietaire === 'string'
          ? JSON.parse(properties.proprietaire)
          : properties.proprietaire;

        const gerant = typeof properties.gerant === 'string'
          ? JSON.parse(properties.gerant)
          : properties.gerant;

        const popupContent = `
          <div class="w-[400px] bg-white rounded-xl overflow-hidden">
            <div class="p-4">
              <div class="flex justify-between items-start">
                <div class="flex space-x-4">
                  <div class="w-2/5">
                    <img 
                      src="${properties.devanture_url || '/depot.jpg'}" 
                      alt="Devanture du dépôt" 
                      class="w-full h-32 object-cover rounded-lg shadow-sm"
                      onerror="this.src='/depot.jpg'"
                    />
                  </div>
                  <div class="w-3/5">
                    <h3 class="text-base font-semibold text-gray-900 mb-1">${properties.title}</h3>
                    <div class="flex items-center mb-2">
                      <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${properties.status === 'Avec numéro d\'autorisation'
            ? 'bg-green-100 text-green-800'
            : 'bg-orange-100 text-orange-800'
          }">
                        ${properties.status}
                      </span>
                    </div>
                    <p class="text-xs text-gray-500 mb-1">${properties.adresse}</p>
                    <p class="text-xs text-gray-500">${properties.commune}, ${properties.region}</p>
                  </div>
                </div>
                <button 
                  onclick="this.closest('.mapboxgl-popup').remove()"
                  class="text-gray-400 hover:text-gray-600 p-1"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div class="mt-3 pt-3 border-t border-gray-100">
                <div class="grid grid-cols-2 gap-2 text-xs text-gray-600">
                  <div>
                    <p><span class="font-medium">N° Autorisation:</span> ${depot_info.numero_autorisation || 'Non spécifié'}</p>
                    <p><span class="font-medium">Date création:</span> ${depot_info.date_creation}</p>
                    <p><span class="font-medium">Surface:</span> ${infrastructure.surface}</p>
                    <p><span class="font-medium">Climatisation:</span> ${infrastructure.climatisation}</p>
                    <p><span class="font-medium">Réfrigérateur:</span> ${infrastructure.refrigerateur}</p>
                  </div>
                  <div>
                    <p><span class="font-medium">Propriétaire:</span> ${proprietaire.nom}</p>
                    <p><span class="font-medium">Tél. Propriétaire:</span> ${proprietaire.telephone}</p>
                    <p><span class="font-medium">Gérant:</span> ${gerant.nom}</p>
                    <p><span class="font-medium">Tél. Gérant:</span> ${gerant.telephone}</p>
                    <p><span class="font-medium">Département:</span> ${properties.departement}</p>
                  </div>
                </div>
              </div>

              <div class="mt-3 pt-3 border-t border-gray-100">
                <div class="text-xs text-gray-600">
                  <p><span class="font-medium">Localité:</span> ${properties.localite}</p>
                  <p><span class="font-medium">Quartier:</span> ${properties.quartier}</p>
                  <p><span class="font-medium">Source d'énergie:</span> ${infrastructure.source_energie}</p>
                </div>
              </div>

              <div class="flex space-x-2 mt-3">
               
                <button 
                  onclick='window.calculateRoute(${JSON.stringify(coordinates)})'
                  class="flex-1 inline-flex items-center justify-center px-2 py-1.5 text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                 >
                  <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>
                  Itinéraire
                </button>
              </div>
            </div>
          </div>
        `;

        new mapboxgl.Popup({
          closeButton: false,
          closeOnClick: false,
          anchor: 'bottom',
          offset: [0, -10],
          className: 'custom-popup'
        })
          .setLngLat(coordinates)
          .setHTML(popupContent)
          .addTo(map.current);
      });

      // Effets de survol
      ['clusters', 'unclustered-point'].forEach(layer => {
        map.current.on('mouseenter', layer, () => {
          map.current.getCanvas().style.cursor = 'pointer';
        });
        map.current.on('mouseleave', layer, () => {
          map.current.getCanvas().style.cursor = '';
        });
      });
    };

    initializeMap();

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [depotsData, communesData, is3DMode]);

  // Fonction pour changer le style de la carte
  const changeMapStyle = (styleId) => {
    if (map.current) {
      map.current.setStyle(`mapbox://styles/mapbox/${styleId}`);
      setCurrentStyle(styleId);
    }
  };

  const handleStatusChange = (e) => {
    const status = e.target.value;
    setSelectedStatus(status);
    
    if (status === 'all') {
      setFilteredCommunes([]);
    } else {
      const filtered = communesData.filter(commune => commune.status === status);
      setFilteredCommunes(filtered);
    }
    setShowModal(true);
  };

  // Ne pas afficher la carte si les données ne sont pas chargées
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des données...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="text-center text-red-600">
          <svg className="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p>{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  // Ne rendre la carte que si les données sont chargées
  if (!depotsData) {
    return null;
  }

  return (
    <div className="relative h-screen">
      <Toaster position="top-right" />
      <div className="absolute bottom-80 left-4 z-10 bg-white p-4 rounded-lg shadow-lg">
        <select
          value={selectedStatus}
          onChange={handleStatusChange}
          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        >
          <option value="all">Toutes les communes</option>
          <option value="eligible">Éligibles</option>
          <option value="ineligible_population">Population insuffisante</option>
          <option value="ineligible_depots">Trop de dépôts</option>
          <option value="ineligible_officine">Officine présente</option>
          <option value="ineligible_urbain">Zone urbaine</option>
        </select>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">
                {selectedStatus === 'eligible' ? 'Communes éligibles' :
                 selectedStatus === 'ineligible_population' ? 'Communes avec population insuffisante' :
                 selectedStatus === 'ineligible_depots' ? 'Communes avec trop de dépôts' :
                 selectedStatus === 'ineligible_officine' ? 'Communes avec officine' :
                 selectedStatus === 'ineligible_urbain' ? 'Communes en zone urbaine' :
                 'Toutes les communes'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700 text-xl"
              >
                ✕
              </button>
            </div>

            {filteredCommunes.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-gray-500 text-lg">
                  Aucune commune ne correspond à ce critère
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredCommunes.map((commune, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
                    <h3 className="font-semibold text-lg text-gray-800 mb-3">{commune.commune}</h3>
                    <div className="space-y-2 text-sm text-gray-600">
                      <div className="flex items-center">
                        <span className="w-24 font-medium">Population:</span>
                        <span>{commune.population.toLocaleString()} habitants</span>
                      </div>
                      <div className="flex items-center">
                        <span className="w-24 font-medium">Dépôts:</span>
                        <span>{commune.depot_count}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="w-24 font-medium">Officine:</span>
                        <span className={commune.has_officine ? "text-green-600" : "text-red-600"}>
                          {commune.has_officine ? "Présente" : "Absente"}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div ref={mapContainer} className="h-full" />

      {/* Sélecteur de style - en haut à droite */}
      <div className="absolute top-4 right-20 z-10">
        <select
          value={currentStyle}
          onChange={(e) => changeMapStyle(e.target.value)}
          className="bg-white px-3 py-2 rounded-lg shadow-sm text-sm border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="satellite-streets-v12">Satellite</option>
          <option value="streets-v12">Rues</option>
          <option value="light-v11">Clair</option>
          <option value="dark-v11">Sombre</option>
          <option value="outdoors-v12">Terrain</option>
        </select>
      </div>

      {/* Légende - en bas à gauche */}
      <div className="absolute bottom-8 left-4 z-10">
        <div className="bg-white/90 backdrop-blur-sm rounded-lg shadow-sm border border-gray-100 p-2">
          <div className="flex items-center space-x-4 text-xs text-gray-600">
            <div className="flex items-center space-x-1">
              <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div>
              <span>Avec numéro d 'autorisation</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-2.5 h-2.5 rounded-full bg-orange-500"></div>
              <span>Sans numéro d'autorisation</span>
            </div>
          </div>
        </div>
      </div>

      {/* Statistiques */}
      <div className="absolute bottom-4 right-4 z-10">
        <div className="bg-white/90 backdrop-blur-sm rounded-lg shadow-sm border border-gray-100 p-3">
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div>
              <span className="text-gray-500">Total des dépôts</span>
              <p className="text-lg font-semibold text-gray-800">
                {depotsData?.features?.length || 0}
              </p>
            </div>
            <div>
              <span className="text-gray-500">Dépôts avec numéro d'autorisation</span>
              <p className="text-lg font-semibold text-green-600">
                {depotsData?.features?.filter(f => f.properties.status === 'Reconnu').length || 0}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapBoxComponent;